import { InquiryStatusFilter } from '@texas/api/endpoints/inquiryApi';
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from 'react';

export interface VersionFilters {
  supplierIds?: number[];
  optionIds?: number[];
  branchIds?: number[];
  cellX?: number;
  cellY?: number;
  showArchived?: boolean;
  inquiryNumber?: string;
  status?: InquiryStatusFilter;
}

interface VersionOverviewContextType {
  filters: VersionFilters;
  setFilters: (filters: VersionFilters) => void;
  updateFilter: <K extends keyof VersionFilters>(
    key: K,
    value: VersionFilters[K],
  ) => void;
}

const VersionOverviewContext = createContext<VersionOverviewContextType | null>(
  null,
);

export function useVersionOverviewContext() {
  const context = useContext(VersionOverviewContext);
  if (!context) {
    throw new Error(
      'useVersionOverviewContext must be used within VersionOverviewProvider',
    );
  }
  return context;
}

interface Props {
  children: ReactNode;
  initialFilters?: VersionFilters;
}

export function VersionOverviewProvider({ children, initialFilters }: Props) {
  const [filters, setFilters] = useState<VersionFilters>(initialFilters ?? {});

  const updateFilter = useCallback(
    <K extends keyof VersionFilters>(key: K, value: VersionFilters[K]) => {
      setFilters((prev) => {
        const previousValue = prev[key];
        const unchanged =
          Array.isArray(previousValue) && Array.isArray(value)
            ? previousValue.length === value.length &&
              previousValue.every((item, index) => item === value[index])
            : previousValue === value;

        return unchanged ? prev : { ...prev, [key]: value };
      });
    },
    [],
  );

  return (
    <VersionOverviewContext.Provider
      value={{ filters, setFilters, updateFilter }}
    >
      {children}
    </VersionOverviewContext.Provider>
  );
}
